import { LoadingOutlined } from '@ant-design/icons';
import { Button, Form, message, Modal, Table, Tabs, Tag } from 'antd';
import { get, isEmpty, omit, pick } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import NumberFormat from 'react-number-format';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { tagTypes } from '../guide';
import {
  NoteWizard,
  noteColumns,
  stepsFor,
} from '../components/note-form/NoteWizard';
import { NOTE_TYPES } from '../components/note-form/fields';
import { isIndividual } from '../components/note-form/noteSteps';
import { NoteHeader } from '../components/note-form/NoteHeader';
import { NoteSystemPanel } from '../components/note-form/NoteSystemPanel';
import { NoteWalletCard } from '../components/treasury/NoteWalletCard';
import { UPDATE_INVESTMENT } from './apollo_mutations';
import {
  ADMIN_INVESTMENT_TRANSACTIONS,
  GET_INVESTMENT,
  TAGS,
} from './apollo_queries';

// Reconnected to constlending-api-next (2026-09-08). GONE with no backend op: payouts, the Equity Trust
// asset buttons and the attach/detach of a document (updateInvestmentDocument). Of the legacy's three
// wallet buttons only "Withdraw" came back (redeemAdmin → the Wallet tab's NoteWalletCard, 2026-09-18);
// "Fund this loan" and "Transfer to Constlending Wallet" (investAdmin) are NOT migrated — TL decision
// 2026-10-30, measured: 4 uses in prod, all in 2021 (backend treasury/README.md § The two siblings). Documents are uploaded with the Save
// (`adminUpdateInvestment.documents`, one `investment_document` row per file) and listed read-only.
// The UPDATE TIMELINE is back (2026-09-12): it is written from the Docs & Updates step with three ops of
// its own (adminAddInvestmentUpdate / adminUpdateInvestmentUpdate / adminRemoveInvestmentUpdate), each
// answering with the note, so it never rides on this page's Save — note-form/NoteUpdates.js.
// No Delete here (TL, 2026-09-09): the backend keeps `adminDeleteInvestment`, the panel does not offer it.
// No Refresh either (TL, 2026-09-12): nothing on this page needs a manual reload. The Save, the two
// operator switches (saveOperation) and the Alto retry in the System tab (refetchNote) all refetch the
// note themselves, and the three update-timeline ops answer WITH the note, so Apollo repaints it from
// the answer. A reload button only suggested the screen might be lying. Leaving is the header's discreet
// link (NoteHeader), not a red button among the actions.
//
// EDITING IS THE SAME FLOW AS CREATING (TL, 2026-09-10): the editable body is `NoteWizard` in `edit`
// mode — the very component Add Property creates with — so both screens walk the same steps, in the same
// order, with the same captures and the same labels; only the confirm button changes ("Save changes").
// Which steps it walks depends on the product: an Individual Note gets the five sections of the
// investor's fact sheet, everything the system mints gets the REDUCED set (note-form/noteSteps.js).
//
// ORGANISED BY NATURE, NOT BY STACKED SECTIONS (TL, 2026-09-12). It used to be one long column — title
// bar, Operations card, wizard, System collapse — where a two-switch card weighed as much as the whole
// fact sheet and the read-only panel was scrolled past on the way to the Save. Three zones now:
//   · NoteHeader — WHO the note is (address, product, listing, Alto, availability) plus the two operator
//     switches as small inline controls and the page's buttons. One band, no cards inside it.
//   · Edit tab   — ONLY the wizard and its Save bar, nothing intercalated.
//   · System tab — what the SYSTEM writes (funded, Alto with its retry, ledger
//     activity, yield history, cycle, dates). TABS and not a side column: the wizard already splits into
//     fields + the fact-sheet screenshot at xl, so a third column would squeeze the form. antd keeps a
//     visited pane mounted, so walking to System and back leaves the form on the same step, unsaved
//     values and all.
// The form carries only what somebody reads on the new system (TL, 2026-09-10): a column that left the
// form is never seeded nor sent, so an update leaves it as stored. This page owns the payload and the Save.
// OPERATOR FILES ARE GONE (TL, 2026-09-12): `appraisal` and `inspectionReports` were legacy lists the
// investor never sees — the dashboard replaced them with the real document collection on 2026-09-05 —
// so the panel stops offering them and the Save stops sending those variables. The columns still exist
// in the backend, untouched by an update that does not name them.

// `Investment` returns these as Float, `AdminInvestmentInput` takes them as String: an untouched initial
// value would otherwise reach the API as a number.
const AS_STRING = [
  'arv',
  'arvLtv',
  'asIsLtv',
  'asIsValuePurchasePrice',
  'netYield',
  'rehabBudget',
];
const AS_NUMBER = ['investmentAmount', 'fundingAmount'];
// Handled apart from the note's own columns: the files, the two tag selects and the date.
const NOT_COLUMNS = [
  'image',
  'documents',
  'maturityDate',
  'loanTag',
  'propertyTag',
];

export function PropertyDetails() {
  const history = useHistory();
  const match = useRouteMatch();
  const [transactionsModal, showTransactionsModal] = useState(false);
  const investmentId = get(match, 'params.id', '');
  const [form] = Form.useForm();
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(10);
  const { data, loading, refetch, error } = useQuery(GET_INVESTMENT, {
    variables: { investmentId },
  });
  // Root query, no longer nested in getInvestment; the backend rejects count > 100.
  const {
    data: transactionsData,
    loading: transactionsLoading,
    error: transactionsError,
  } = useQuery(ADMIN_INVESTMENT_TRANSACTIONS, {
    variables: { investmentId, page, count: Math.min(pageCount, 100) },
  });
  const { data: tagData, loading: tagLoading } = useQuery(TAGS);
  const [update, i] = useMutation(UPDATE_INVESTMENT);

  const investment = get(data, 'getInvestment', null);
  const transactions = get(
    transactionsData,
    'adminInvestmentTransactions.items',
    []
  );
  const totalTransaction = get(
    transactionsData,
    'adminInvestmentTransactions.total',
    0
  );

  if (loading || tagLoading) {
    return (
      <div className="web-load-wrap">
        <LoadingOutlined />
      </div>
    );
  }

  if (error || !investment) {
    return (
      <div className="web-chat__main-content">
        Error, please refresh the page
      </div>
    );
  }

  const onChange = (pagination, _, sorter) => {
    if (pagination.current !== page) {
      setPage(pagination.current);
      history.push(`?p=${pagination.current}`);
    }

    if (pageCount !== pagination.pageSize) {
      setPageCount(pagination.pageSize);
    }
  };

  // Only the columns the step set in play actually shows: a box the reduced set drops is never seeded,
  // so it never travels back and the backend leaves it exactly as stored.
  const initialValues = pick(
    investment,
    noteColumns(stepsFor(investment.noteType, 'edit'))
  );
  // The two tag selects belong to the Individual Note's fact sheet. The reduced set has no tag box, so
  // they are not seeded there either — otherwise a Save would post an empty `tags` and wipe what the
  // note carries without anybody having touched a box.
  const hasTagBoxes = isIndividual(investment.noteType);

  const columns = [
    {
      title: 'Transaction Type',
      dataIndex: 'type',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      render: (amt) => (
        <NumberFormat
          displayType="text"
          value={parseFloat(amt)}
          thousandSeparator={true}
          prefix={'$'}
          renderText={(value) => <div>{value}</div>}
        />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) =>
        status === 'success' ? (
          <Tag color="green">{status.toUpperCase()}</Tag>
        ) : (
          <Tag color="red">{status.toUpperCase()}</Tag>
        ),
    },
    {
      title: 'Sender',
      dataIndex: 'userAccount',
      render: (u, { user }) => (
        <span>
          {u?.accountName ||
            [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
            '-'}
        </span>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'user',
      render: (user) => <span>{user?.email || ''}</span>,
    },
    {
      title: 'Create',
      dataIndex: 'createdAt',
      render: (createdAt) => <span>{moment(createdAt).format('LLL')}</span>,
    },
  ];

  const tags = investment.typedTags || [];
  let propertyTagLocal = '';
  let loanTagLocal = '';

  if (!isEmpty(tags)) {
    const ls = tags.filter((t) => t && t.type === tagTypes.loan.name);
    const ps = tags.filter((t) => t && t.type === tagTypes.property.name);

    if (!isEmpty(ls)) {
      loanTagLocal = ls[0]?._id;
    }

    if (!isEmpty(ps)) {
      propertyTagLocal = ps[0]?._id;
    }
  }

  const allTags = tagData?.tags || [];

  const loanTags = allTags.filter((t) => t.type === tagTypes.loan.name);
  const propTags = allTags.filter((t) => t.type === tagTypes.property.name);

  const onFinish = async (values) => {
    const { image, maturityDate, documents, loanTag, propertyTag } = values;

    const tags = [];

    if (loanTag) {
      tags.push(loanTag);
    }

    if (propertyTag) {
      tags.push(propertyTag);
    }

    const data = omit(values, NOT_COLUMNS);

    const payload = { data };
    // Only a step set that HAS the tag selects may rewrite the tags: an absent `tags` leaves them stored.
    if (hasTagBoxes) {
      payload.data['tags'] = tags;
    }

    payload['investmentId'] = investmentId;

    if (!isEmpty(image)) {
      payload['image'] = image[0]['originFileObj'];
    }

    if (maturityDate) {
      payload['data']['maturityDate'] = moment(maturityDate).format();
    }
    const documentFiles = (documents || []).filter(
      (item) => item?.originFileObj
    );
    if (!isEmpty(documentFiles)) {
      payload['documents'] = documentFiles.map((item) => item['originFileObj']);
    }

    AS_STRING.forEach((field) => {
      if (typeof payload.data[field] === 'number') {
        payload.data[field] = payload.data[field].toString();
      }
    });

    // An empty amount is left out so the backend keeps what it has; 0 travels as 0. A step set that
    // never asked for the amount does not carry it at all, and nothing is deleted for it either.
    AS_NUMBER.forEach((field) => {
      if (!(field in payload.data)) return;
      const amount = parseFloat(values[field]);
      if (Number.isNaN(amount)) {
        delete payload.data[field];
      } else {
        payload.data[field] = amount;
      }
    });

    try {
      const { data } = await update({ variables: payload });

      if (data) {
        message.info('Update success');
        form.setFieldsValue({ documents: [] });
      }

      await refetch();
    } catch (error) {
      // Backend domain errors (INVESTMENT_FIELD_LOCKED, INVESTMENT_NOTE_LISTING_TAKEN, ...) carry their copy.
      message.error(error.message.replace(/^GraphQL error: /, ''));
    }
  };

  // One operator action = one column. The same `adminUpdateInvestment`, with a payload of ONE field, so
  // nothing typed in the form (saved or not) rides along with the click.
  const saveOperation = async (data) => {
    try {
      await update({ variables: { investmentId, data } });
      await refetch();
      message.success('Saved');
    } catch (error) {
      message.error(error.message.replace(/^GraphQL error: /, ''));
    }
  };

  // `overflow: visible` keeps the SaveBar sticking to the real scroller (section.ant-layout): an
  // ancestor that clips would turn the sticky bar into a plain one. The page is already inside the
  // shell's white card, so the header is a band with a rule under it, not one more Card.
  return (
    <div
      className="web-chat__main-content"
      style={{ overflow: 'visible', display: 'block' }}
    >
      <Modal
        width={1100}
        title="Transactions"
        visible={transactionsModal}
        onOk={() => showTransactionsModal(false)}
        onCancel={() => showTransactionsModal(false)}
      >
        {transactionsError && (
          <div style={{ color: '#cf1322', marginBottom: 12 }}>
            {`Could not load the movements: ${transactionsError.message.replace(
              /^GraphQL error: /,
              ''
            )}`}
          </div>
        )}
        <Table
          dataSource={transactions}
          columns={columns}
          rowKey="id"
          onChange={onChange}
          locale={{ emptyText: 'No movements on this note yet' }}
          pagination={{
            total: totalTransaction,
            position: ['bottomCenter'],
            current: page,
            pageSize: pageCount,
            pageSizeOptions: ['10', '20', '30', '50'],
            hideOnSinglePage: true,
          }}
          loading={transactionsLoading}
        />
      </Modal>

      <NoteHeader
        investment={investment}
        onSave={saveOperation}
        onBack={() => history.push('/properties-and-investments')}
        actions={
          <Button
            role="button"
            onClick={() => showTransactionsModal(true)}
            type="primary"
          >
            Transactions
          </Button>
        }
      />

      <Tabs defaultActiveKey="edit">
        <Tabs.TabPane tab="Edit" key="edit">
          <NoteWizard
            mode="edit"
            form={form}
            investment={investment}
            initialValues={{
              ...initialValues,
              ...(hasTagBoxes
                ? { propertyTag: propertyTagLocal, loanTag: loanTagLocal }
                : {}),
              maturityDate: investment.maturityDate
                ? moment(investment.maturityDate)
                : null,
            }}
            propTags={propTags}
            loanTags={loanTags}
            onFinish={onFinish}
            loading={i.loading}
            submitLabel="Save changes"
            reviewIntro="Every box of every step, together — change any of them right here before saving."
          />
        </Tabs.TabPane>
        {/* The note's own money (2026-09-18): its live Sila wallet and the withdrawal to a bank of the
            house — a different nature of thing from the fact sheet, so a tab of its own like System.
            The Credit Fund has no wallet (decision 2026-10-27), so it gets no Wallet tab. */}
        {investment.noteType !== NOTE_TYPES.creditFund && (
          <Tabs.TabPane tab="Wallet" key="wallet">
            <NoteWalletCard
              investmentId={investmentId}
              noteName={investment.address}
            />
          </Tabs.TabPane>
        )}
        <Tabs.TabPane tab="System" key="system">
          <NoteSystemPanel investment={investment} refetchNote={refetch} />
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
}
